import { createRouter } from '@tanstack/react-router';
import { dehydrate, hydrate } from '@tanstack/react-query';
import { logger } from '@repo/shared/common';
import { createAppQueryClient } from './lib/query-client';
import { RoutePending } from './components/shell/RoutePending';
import { RouteError } from './components/shell/RouteError';
import { RouteNotFound } from './components/shell/RouteNotFound';
import { routeTree } from './routeTree.gen';

declare const __SENTRY_DSN__: string;
declare const __MIXPANEL_TOKEN__: string;
declare const __APP_ENV__: string;

if (typeof window === 'undefined') {
  void import('@repo/shared/server')
    .then(({ installServerErrorHandlers, initSentryServer }) => {
      installServerErrorHandlers();
      if (__SENTRY_DSN__) {
        initSentryServer();
      } else {
        logger.warn('SENTRY_DSN not configured, error tracking using logger fallback');
      }
    })
    .catch((err: unknown) => {
      // eslint-disable-next-line no-console -- last resort: server init not yet available
      console.error('Failed to initialize server', err);
    });
} else if (__SENTRY_DSN__) {
  void import('@repo/shared/client')
    .then(({ initSentryClient }) => {
      initSentryClient({
        dsn: __SENTRY_DSN__,
        environment: __APP_ENV__ || 'development',
      });
    })
    .catch((err: unknown) => {
      // eslint-disable-next-line no-console -- last resort: client init not yet available
      console.error('Failed to initialize client Sentry', err);
    });
} else {
  logger.warn('SENTRY_DSN not configured, client error tracking using logger fallback');
}

if (typeof window !== 'undefined') {
  void import('@repo/shared/client')
    .then(({ initAnalytics }) => {
      initAnalytics({ token: __MIXPANEL_TOKEN__ || undefined });
    })
    .catch((err: unknown) => {
      // eslint-disable-next-line no-console -- last resort: analytics init is best-effort
      console.error('Failed to initialize analytics', err);
    });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- inferred type is captured via ReturnType<typeof getRouter> in Register below; spelling it out would be fragile
export function getRouter() {
  // One QueryClient per router. `getRouter()` runs per-request on the server
  // (so each request gets an isolated cache) and once on the client. The same
  // instance is placed on the router `context` — where loaders/beforeLoad read
  // it via `context.queryClient` — and provided to React in `__root.tsx`.
  const queryClient = createAppQueryClient();

  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // When a route's loader data is stale on navigation, await the fresh load
    // rather than flashing the stale cached data and revalidating in the
    // background (TanStack's default 'background'). This removes the
    // stale-then-update flicker; the 0ms top progress bar + the 600ms pending
    // component below cover the wait. Only stale reloads pay this cost — a fresh
    // (within-staleTime) revisit still renders instantly with no reload.
    defaultStaleReloadMode: 'blocking',
    // Router-driven loading/error/404 UI. The pending spinner only shows if a
    // route's awaited work exceeds 600ms (uncommon — the routinely-awaited work is
    // auth, plus the one-time org resolve on post-sign-in); the 0ms top progress
    // bar (mounted in __root) covers the common fast navigation.
    defaultPendingMs: 600,
    defaultPendingMinMs: 500,
    defaultPendingComponent: RoutePending,
    defaultErrorComponent: RouteError,
    defaultNotFoundComponent: RouteNotFound,
    scrollRestoration: true,
    // The AppShell card is fixed-height and the page (window) never scrolls — the
    // scroll region is the `<main data-scroll-restoration-id="app-content">` inside
    // it (AppShell.tsx). TanStack's default scroll-to-top targets `window`, which is
    // a no-op here, so without this a forward navigation to a not-yet-visited route
    // would inherit the previous screen's scroll offset. Naming the container both
    // resets it to top on forward nav and gives the save/restore cache a stable key
    // (instead of a hashed CSS-module selector).
    scrollToTopSelectors: ['[data-scroll-restoration-id="app-content"]'],
    // SSR ↔ Query bridge: the server serializes the Query cache (everything a
    // loader prefetched) into the streamed HTML; the client rehydrates it into
    // the same QueryClient before the first render, so `useQuery` reads warm
    // data instead of refetching on mount. This is what turns a server-awaited
    // loader into a flash-free first paint.
    //
    // @ts-expect-error -- react-query's DehydratedState types `mutationKey` as
    // `readonly unknown[]`, whose elements TanStack Router's ValidateSerializableInput
    // rejects (`unknown`/`{}` ⊄ a serializable value). The value is fully serializable
    // at runtime; this is a pure type mismatch between the two libraries, and the
    // error fires regardless of `exactOptionalPropertyTypes`. See docs/IMPLEMENTATION_NOTES.md.
    dehydrate: () => ({ queryClient: dehydrate(queryClient) }),
    hydrate: (dehydrated) => {
      hydrate(queryClient, dehydrated.queryClient);
    },
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
